/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import _ from 'lodash';
import * as z from 'zod';
import { compoundVersionIdSchemaZ } from '../models/components';
import { fromObjectEntries } from '../utils';
const STEP_DELTA_ROUNDING = 1e5;
/**
 * Zod equivalent of the backend `BatchableInputNumber` pydantic model.
 *
 * On the wire a batchable number is either a single number (start) or a
 * `[start, end, step]` tuple. Mirrors the backend validators:
 *   - start >= 0
 *   - end and step must both be specified or both omitted (null)
 *   - end > 0, step > 0
 *   - step <= round(end - start, 5)
 *   - end lands on an exact step increment from start
 */
export const batchableInputNumberSchema = z.union([
    z.number().min(0, 'Start must be ≥ 0'),
    z
        .array(z.number().nullable())
        .length(3, 'Range must be [start, end, step]')
        .superRefine((tuple, ctx) => {
        const [start, end, step] = tuple;
        if (start === null || start < 0) {
            ctx.addIssue({ code: 'custom', message: 'Start must be ≥ 0' });
        }
        const endSet = end !== null;
        const stepSet = step !== null;
        if (endSet !== stepSet) {
            ctx.addIssue({
                code: 'custom',
                message: 'End and step when batching must both be specified',
            });
        }
        if (endSet && end <= 0) {
            ctx.addIssue({ code: 'custom', message: 'End must be greater than 0' });
        }
        if (stepSet && step <= 0) {
            ctx.addIssue({ code: 'custom', message: 'Step must be greater than 0' });
        }
        if (endSet && stepSet && start !== null) {
            const delta = Math.round((end - start) * STEP_DELTA_ROUNDING) / STEP_DELTA_ROUNDING;
            if (step > delta) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Step value must be less than or equal to delta between start and end',
                });
                return;
            }
            const scaledDelta = Math.round(delta * STEP_DELTA_ROUNDING);
            const scaledStep = Math.round(step * STEP_DELTA_ROUNDING);
            if (scaledStep === 0 || scaledDelta % scaledStep !== 0) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'End must land on a step increment from start',
                });
            }
        }
    }),
]);
/**
 * A single `batch_params` value: a batchable number, an auto-sizing boolean
 * flag (e.g. feeder size_to_asset_*), or an explicit value list (thermal
 * fuel-price / annual fields). Matches the dynamically generated backend
 * PlantSimBatchParams model (extra="allow").
 */
export const plantSimBatchParamValueSchema = z.union([
    z.boolean(),
    batchableInputNumberSchema,
    z.array(z.union([z.number(), z.string()]).nullable()),
    z.null(),
]);
export const plantSimBatchParamsSchema = z.record(z.string(), plantSimBatchParamValueSchema);
export const plantSimBatchSubmitRequestSchema = z.object({
    config_id: compoundVersionIdSchemaZ,
    custom_batch_id: z.string().nullable().optional(),
    batch_params: plantSimBatchParamsSchema.optional(),
});
const PLANT_SIM_BATCH_MAX_ESS_COUNT = 5;
const PLANT_SIM_BATCH_MAX_PV_COUNT = 50;
const PLANT_SIM_BATCH_MAX_THERMAL_COUNT = 50;
const PLANT_SIM_BATCH_MAX_AS_AVAILABLE_COUNT = 50;
export const plantSimBatchParamLabels = {
    poi_mw: 'POI MW',
    ...fromObjectEntries(_.range(1, PLANT_SIM_BATCH_MAX_PV_COUNT + 1).flatMap((i) => [
        [`pv_${i}_n_inverters`, `PV ${i} Inverters`],
        [`pv_${i}_ilr`, `PV ${i} ILR`],
    ])),
    ...fromObjectEntries(_.range(1, PLANT_SIM_BATCH_MAX_ESS_COUNT + 1).flatMap((i) => [
        [`ess_${i}_power`, `ESS ${i} Power`],
        [`ess_${i}_duration`, `ESS ${i} Duration`],
    ])),
    ...fromObjectEntries(_.range(1, PLANT_SIM_BATCH_MAX_THERMAL_COUNT + 1).flatMap((i) => [
        [`thermal_${i}_unit_count`, `Thermal ${i} Unit Count`],
        [`thermal_${i}_design_mw`, `Thermal ${i} Design MW`],
        [
            `thermal_${i}_annual_capacity_factor`,
            `Thermal ${i} Annual Capacity Factor %`,
        ],
        [
            `thermal_${i}_annual_energy_max`,
            `Thermal ${i} Annual Energy Max MWh/year`,
        ],
    ])),
    ...fromObjectEntries(_.range(1, PLANT_SIM_BATCH_MAX_AS_AVAILABLE_COUNT + 1).map((i) => [
        `wind_${i}_n_turbines`,
        `Wind ${i} Turbines`,
    ])),
    shape_scale: 'Shape Scale',
};
//# sourceMappingURL=batch.js.map